import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import type { QueryClient } from "@tanstack/react-query";
import { apiClient } from "@/integrations/api/client/client";

/**
 * SignalR client for the backend `case-events` hub.
 *
 * The hub emits two events:
 *   - `case.created`        — a new case is queued for review.
 *   - `case.statusChanged`  — an existing case transitioned status (e.g. accepted/rejected).
 *
 * On either event we invalidate the `case-reviews` and `patient-queue` queries
 * so the queue panels refresh without waiting for the 15s polling fallback.
 *
 * The hub URL is derived from `VITE_API_BASE_URL` so the same env var the
 * REST client uses also configures realtime.
 */

const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) || "http://localhost:5108";

const HUB_URL = `${API_BASE_URL.replace(/\/$/, "")}/hubs/case-events`;

let activeConnection: HubConnection | null = null;

const invalidateQueues = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ["case-reviews"] });
  queryClient.invalidateQueries({ queryKey: ["patient-queue"] });
};

export async function connectCaseEventsHub(queryClient: QueryClient): Promise<HubConnection | null> {
  // If a connection is already up (or being brought up) reuse it — repeated
  // calls from re-renders must not stack hubs.
  if (
    activeConnection &&
    (activeConnection.state === HubConnectionState.Connected ||
      activeConnection.state === HubConnectionState.Connecting ||
      activeConnection.state === HubConnectionState.Reconnecting)
  ) {
    return activeConnection;
  }

  const connection = new HubConnectionBuilder()
    .withUrl(HUB_URL, {
      // Token is read lazily on (re)connect so it stays in sync with rotations.
      accessTokenFactory: () => apiClient.getToken() ?? "",
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Warning)
    .build();

  connection.on("case.created", () => invalidateQueues(queryClient));
  connection.on("case.statusChanged", () => invalidateQueues(queryClient));

  try {
    await connection.start();
    activeConnection = connection;
    return connection;
  } catch (err) {
    // Realtime is best-effort: polling fallback in `useCaseReviews` /
    // `usePatientQueue` keeps the UI fresh if the hub is unreachable.
     
    console.warn("[realtime] case-events hub connection failed", err);
    activeConnection = null;
    return null;
  }
}

export async function disconnectCaseEventsHub(): Promise<void> {
  if (!activeConnection) return;
  try {
    await activeConnection.stop();
  } catch {
    // ignore stop errors
  }
  activeConnection = null;
}
